import { useRef, useState, useEffect, useCallback, type UIEvent } from 'react';
import { Bell } from 'lucide-react';
import { useInfiniteNotifications, useMarkAllRead, useMarkNotificationRead } from '@/api/notifications';
import { cn } from '@/components/ui';
import type { NotificationDto } from '@/types/notification';

/** Fetch the next page once the list is scrolled within this many px of the bottom. */
const LOAD_MORE_THRESHOLD_PX = 48;

function relativeTime(isoString: string): string {
  const diff = Date.now() - new Date(isoString).getTime();
  const seconds = Math.floor(diff / 1000);
  if (seconds < 60) return `${seconds}s ago`;
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes}m ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h ago`;
  return `${Math.floor(hours / 24)}d ago`;
}

const SEVERITY_CLASSES: Record<string, string> = {
  critical: 'bg-state-danger/15 text-state-danger',
  high:     'bg-state-warning/15 text-state-warning',
  medium:   'bg-yellow-500/15 text-yellow-400',
  low:      'bg-blue-500/15 text-blue-400',
  info:     'bg-surface-2 text-text-secondary',
};

function severityClass(severity: string): string {
  return SEVERITY_CLASSES[severity.toLowerCase()] ?? SEVERITY_CLASSES.info;
}

function NotificationRow({
  notification,
  onMarkRead,
  isMarking,
}: {
  notification: NotificationDto;
  onMarkRead: (id: string) => void;
  isMarking: boolean;
}) {
  return (
    <div
      onClick={() => !notification.read && onMarkRead(notification.id)}
      className={cn(
        'flex items-start gap-2 py-2 px-3 border-b border-border-subtle last:border-0',
        !notification.read && 'bg-accent/5 cursor-pointer hover:bg-accent/10',
      )}
      title={notification.read ? undefined : 'Click to mark as read'}
    >
      <span
        aria-hidden
        className={cn(
          'mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full',
          notification.read ? 'bg-transparent' : 'bg-accent',
        )}
      />
      <span
        className={cn(
          'mt-0.5 shrink-0 rounded px-1.5 py-0.5 text-[10px] font-semibold uppercase',
          severityClass(notification.severity),
        )}
      >
        {notification.severity}
      </span>
      <div className="flex-1 min-w-0">
        <p
          className={cn(
            'text-xs truncate',
            notification.read ? 'font-normal text-text-secondary' : 'font-medium text-text-primary',
          )}
          title={notification.title}
        >
          {notification.title}
        </p>
        <p className="text-[10px] text-text-tertiary mt-0.5">{relativeTime(notification.createdAt)}</p>
      </div>
      {!notification.read && isMarking && (
        <span className="text-[10px] text-text-tertiary shrink-0">…</span>
      )}
    </div>
  );
}

export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);

  // API — primary data source, paged 20 at a time and fetched further as the
  // dropdown list is scrolled (see handleListScroll below)
  const {
    data,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteNotifications({ unreadOnly: false, limit: 20 });
  const markAllReadMutation = useMarkAllRead();
  const markReadMutation = useMarkNotificationRead();

  const apiNotifications = data?.pages.flat();

  // Sort API notifications newest-first
  const sortedNotifications = apiNotifications
    ? [...apiNotifications].sort(
        (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
      )
    : [];

  const handleListScroll = useCallback(
    (e: UIEvent<HTMLDivElement>) => {
      if (!hasNextPage || isFetchingNextPage) return;
      const el = e.currentTarget;
      const distanceFromBottom = el.scrollHeight - el.scrollTop - el.clientHeight;
      if (distanceFromBottom < LOAD_MORE_THRESHOLD_PX) {
        fetchNextPage();
      }
    },
    [hasNextPage, isFetchingNextPage, fetchNextPage],
  );

  // Badge is scoped to unread rows from the notifications API only — the
  // only notification type currently persisted is endpoint_offline.
  const totalUnread = apiNotifications?.filter((n) => !n.read).length ?? 0;

  const handleToggle = useCallback(() => {
    setOpen((prev) => !prev);
  }, []);

  const handleMarkRead = useCallback(
    (id: string) => {
      markReadMutation.mutate(id);
    },
    [markReadMutation],
  );

  const handleMarkAllRead = useCallback(() => {
    markAllReadMutation.mutate(undefined, {
      onSuccess: () => {
        setOpen(false);
      },
    });
  }, [markAllReadMutation]);

  useEffect(() => {
    if (!open) return;
    const handler = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);

  return (
    // h-12 matches AppLayout's header height exactly, so the row this sits in
    // is forced to that same height and centered with zero offset
    <div ref={containerRef} className="relative flex h-12 items-center">
      <button
        onClick={handleToggle}
        className="relative inline-flex h-7 w-7 items-center justify-center rounded-sm text-text-secondary hover:bg-surface-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-border-focus"
        aria-label={`Notifications${totalUnread > 0 ? ` (${totalUnread} unread)` : ''}`}
      >
        <Bell className={cn('h-3.5 w-3.5', totalUnread > 0 && 'animate-pulse text-state-danger')} />
        {totalUnread > 0 && (
          <span className="absolute -right-0.5 -top-0.5 flex h-4 min-w-[16px] animate-pulse items-center justify-center rounded-full bg-state-danger px-0.5 text-[10px] font-semibold text-white">
            {totalUnread > 99 ? '99+' : totalUnread}
          </span>
        )}
      </button>

      {open && (
        // top-full now measures from the h-12 wrapper above (not the smaller
        // button), so it lands exactly on the header's bottom border.
        <div className="absolute right-0 top-full z-50 w-72 rounded-md border border-border-subtle bg-surface-0 shadow-lg">
          <div className="flex items-center justify-between px-3 py-2 border-b border-border-subtle">
            <span className="text-xs font-semibold text-text-primary">Notifications</span>
          </div>

          {sortedNotifications.length === 0 ? (
            <p className="px-3 py-4 text-center text-xs text-text-tertiary">No recent notifications</p>
          ) : (
            <div className="max-h-72 overflow-y-auto" onScroll={handleListScroll}>
              {sortedNotifications.map((notification) => (
                <NotificationRow
                  key={notification.id}
                  notification={notification}
                  onMarkRead={handleMarkRead}
                  isMarking={markReadMutation.isPending && markReadMutation.variables === notification.id}
                />
              ))}
              {isFetchingNextPage && (
                <p className="py-2 text-center text-[10px] text-text-tertiary">Loading more…</p>
              )}
            </div>
          )}

          <div className="px-3 py-2 border-t border-border-subtle">
            <button
              onClick={handleMarkAllRead}
              disabled={markAllReadMutation.isPending}
              className="w-full rounded-sm py-1 text-[11px] font-medium text-text-secondary hover:text-text-primary hover:bg-surface-1 transition-colors disabled:opacity-50"
            >
              {markAllReadMutation.isPending ? 'Marking…' : 'Mark all read'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
